import {bugDecisionReadConfirms,runBugDecision,sendBugDecision} from './bug-queue-action.js';
import {initFilesManagement} from './files-management.js';
import {loginDestination} from './files-login.js';
import {initOwnerAccess} from './owner-access.js';
import {initEmployeeMail,employeeMailErrors} from './employee-mail.js';
import {initFilesAccessEditor} from './files-access-editor.js';
import {initEmployeeDiscoverability} from './employeeDiscoverability.js';
import {initMailManagement} from './mail-management.js';
import {initMailOpen} from './mail-open.js';
async function sendJson(method: string, url: string, body: unknown): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  const res = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let data: Record<string, unknown> = {};
  try {
    data = await res.json();
  } catch {
    // no body
  }
  return { ok: res.ok, status: res.status, data };
}

async function postJson(url: string, body: unknown): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  return sendJson('POST', url, body);
}

async function patchJson(url: string, body: unknown): Promise<{ ok: boolean; status: number; data: Record<string, unknown> }> {
  return sendJson('PATCH', url, body);
}

function showError(el: HTMLElement | null, message: string): void {
  if (!el) return;
  el.textContent = message;
  el.classList.remove('hidden');
}

function fullName(firstName: string, lastName: string | null): string {
  return lastName ? `${firstName} ${lastName}` : firstName;
}

function formatDateOnly(isoDate: string): string {
  const [year, month, day] = isoDate.split('-');
  return year && month && day ? `${day}.${month}.${year}` : isoDate;
}

// last_login_at is an instant, not a calendar date, so it is rendered in the viewer's own zone
// rather than split by hand the way birthday is -- an evening login in Moscow is otherwise shown
// as the previous day, because the server stores UTC.
function formatTimestamp(value: string | null): string {
  if (!value) return 'ещё не входил';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'ещё не входил'
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function hideError(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.add('hidden');
}

const LAST_LOGIN_STORAGE_KEY = 'zoan_last_login';

function rememberLastLogin(email: string): void {
  try {
    localStorage.setItem(LAST_LOGIN_STORAGE_KEY, JSON.stringify({ email }));
  } catch {
    // localStorage unavailable (private mode, blocked) -- not remembering is fine
  }
}

function loadLastLogin(): { email: string } {
  try {
    const raw = localStorage.getItem(LAST_LOGIN_STORAGE_KEY);
    if (!raw) return { email: '' };
    const parsed = JSON.parse(raw) as { email?: unknown };
    return { email: typeof parsed.email === 'string' ? parsed.email : '' };
  } catch {
    return { email: '' };
  }
}

const SIGNUP_ERRORS: Record<string, string> = {
  invalid_company_name: 'Укажите название компании.',
  invalid_slug: 'Короткое имя компании должно состоять из латинских букв, цифр и дефисов.',
  slug_too_short: 'Короткое имя слишком короткое — нужно не меньше четырёх символов.',
  slug_reserved: 'Это имя занято служебным именем. Выберите другой.',
  slug_not_allowed: 'Это имя недоступно. Выберите другой.',
  invalid_email: 'Проверьте email.',
  invalid_first_name: 'Укажите имя.',
  invalid_last_name: 'Укажите фамилию.',
  weak_password: 'Пароль должен быть не короче 10 символов.',
  too_many_requests: 'Слишком много регистраций за короткое время. Попробуйте через час.',
  slug_taken: 'Это короткое имя уже занято.',
  email_taken: 'Этот email уже используется в этой компании.',
  signup_failed: 'Не удалось создать пространство. Попробуйте ещё раз.',
};

function initSignupForm(): void {
  const form = document.getElementById('signup-form');
  if (!(form instanceof HTMLFormElement)) return;
  const errorEl = document.getElementById('signup-error');

  const visitReady = consentReady.then(() => fetch('/api/signup-visit', {
    method: 'POST', credentials: 'same-origin', signal: AbortSignal.timeout(3000),
  })).catch(() => undefined);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError(errorEl);
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (button?.disabled) return;
    if (button) button.disabled = true;
    try {
      await visitReady;
      const data = Object.fromEntries(new FormData(form).entries());
      const slug = String(data.slug ?? '').trim().toLowerCase();
      const result = await postJson('/api/signup', { ...data,chatEnabled:(form.elements.namedItem('chatEnabled') as HTMLInputElement).checked, ...(slug ? { slug } : { slug: undefined }) });
      if (!result.ok) {
        const code = String(result.data.error ?? 'signup_failed');
        showError(errorEl, SIGNUP_ERRORS[code] ?? 'Не удалось создать пространство.');
        return;
      }
      sessionStorage.setItem('zoan_pending_slug', String(result.data.slug));
      sessionStorage.setItem('zoan_pending_email', String(data.email ?? ''));
      window.location.href = '/verify-email';
    } catch {
      showError(errorEl, 'Не удалось связаться с сервером. Проверьте соединение и попробуйте ещё раз.');
    } finally { if (button) button.disabled = false; }
  });
}

function initProfileSetup(me: { firstName: string; lastName: string | null; email: string; profileComplete: boolean }): void {
  const profile = document.getElementById('profile-setup');
  const profileForm = document.getElementById('profile-setup-form');
  if (!me.profileComplete) profile?.classList.remove('hidden');
  if (profileForm instanceof HTMLFormElement) profileForm.addEventListener('submit', async event => {
    event.preventDefault();
    const error = document.getElementById('profile-setup-error');
    hideError(error);
    const button = profileForm.querySelector('button');
    if (button?.disabled) return;
    if (button) button.disabled = true;
    try {
      const data = Object.fromEntries(new FormData(profileForm).entries());
      const result = await patchJson('/api/profile', data);
      if (!result.ok) { showError(error, 'Проверьте имя и попробуйте ещё раз.'); return; }
      profile?.classList.add('hidden');
      const user = document.getElementById('dashboard-user');
      if (user) user.textContent = `${fullName(String(data.firstName).trim(), String(data.lastName ?? '').trim())} · ${me.email}`;
    } catch { showError(error, 'Не удалось сохранить имя. Попробуйте ещё раз.'); }
    finally { if (button) button.disabled = false; }
  });
}

async function loadFunnel(): Promise<void> {
  const target = document.getElementById('funnel-stats');
  if (!target) return;
  try {
    const res = await fetch('/api/super-admin/funnel', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('unavailable');
    const data = await res.json() as Record<string, number | boolean>;
    const stages = [['visited', 'Открыли регистрацию'], ['registered', 'Создали компанию'],
      ['verified', 'Подтвердили почту'], ['colleague_added', 'Добавили сотрудника'],
      ['group_created', 'Создали общий чат'], ['first_message', 'Отправили сообщение']];
    target.replaceChildren(...stages.map(([key, label]) => {
      const item = document.createElement('li');
      item.className = 'rounded-lg border border-slate-700 p-3';
      item.textContent = `${label}: ${data[key!] ?? 0}`;
      return item;
    }));
    const note = document.getElementById('funnel-note');
    if (note) note.textContent = 'Браузеры, впервые открывшие регистрацию за последние 30 дней, и их первая компания. Сбор начат 02.10.2026; старые регистрации не включены.'
      + (data.chatAvailable ? '' : ' Данные чата не обновились; показаны только ранее подтверждённые действия.');
  } catch { target.textContent = 'Статистика пока недоступна.'; }
}

function initVerifyForm(): void {
  const form = document.getElementById('verify-form');
  if (!(form instanceof HTMLFormElement)) return;
  const errorEl = document.getElementById('verify-error');
  const subtitle = document.getElementById('verify-subtitle');

  const slug = sessionStorage.getItem('zoan_pending_slug');
  const email = sessionStorage.getItem('zoan_pending_email');
  if (!slug || !email) {
    window.location.href = '/signup';
    return;
  }
  if (subtitle) subtitle.textContent = `Мы отправили код на ${email}.`;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError(errorEl);
    const code = String(new FormData(form).get('code') ?? '');
    const result = await postJson('/api/auth/verify-email', { slug, email, code });
    if (!result.ok) {
      showError(errorEl, 'Неверный или истёкший код.');
      return;
    }
    sessionStorage.removeItem('zoan_pending_slug');
    sessionStorage.removeItem('zoan_pending_email');
    window.location.href = '/dashboard';
  });
}

function initLoginPage(): void {
  const passwordFormEl = document.getElementById('login-password-form');
  const codeRequestForm = document.getElementById('login-code-request-form');
  const codeConfirmForm = document.getElementById('login-code-confirm-form');
  if (!(passwordFormEl instanceof HTMLFormElement)) return;
  const passwordForm: HTMLFormElement = passwordFormEl;

  const tabPassword = document.getElementById('tab-password');
  const tabCode = document.getElementById('tab-code');

  function showPasswordTab(): void {
    passwordForm.classList.remove('hidden');
    codeRequestForm?.classList.add('hidden');
    codeConfirmForm?.classList.add('hidden');
    tabPassword?.classList.add('bg-white', 'shadow-sm');
    tabPassword?.classList.remove('text-slate-600');
    tabCode?.classList.remove('bg-white', 'shadow-sm');
    tabCode?.classList.add('text-slate-600');
  }

  function showCodeTab(): void {
    passwordForm.classList.add('hidden');
    codeConfirmForm?.classList.add('hidden');
    codeRequestForm?.classList.remove('hidden');
    tabCode?.classList.add('bg-white', 'shadow-sm');
    tabCode?.classList.remove('text-slate-600');
    tabPassword?.classList.remove('bg-white', 'shadow-sm');
    tabPassword?.classList.add('text-slate-600');
  }

  tabPassword?.addEventListener('click', showPasswordTab);
  tabCode?.addEventListener('click', showCodeTab);

  const remembered = loadLastLogin();
  for (const id of ['email-p', 'email-c', 'email-r']) {
    const input = document.getElementById(id);
    if (input instanceof HTMLInputElement) input.value = remembered.email;
  }

  const passwordError = document.getElementById('login-password-error');
  const companyPicker = document.getElementById('login-company-picker');
  const companySelect = document.getElementById('login-company-select');
  passwordForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError(passwordError);
    const data = Object.fromEntries(new FormData(passwordForm).entries());
    const email = String(data.email ?? '');
    rememberLastLogin(email);
    const result = await postJson('/api/auth/login', data);
    if (result.status === 409 && result.data.error === 'multiple_companies' && companyPicker && companySelect instanceof HTMLSelectElement) {
      const companies = Array.isArray(result.data.companies) ? result.data.companies as { slug: string; name: string }[] : [];
      companySelect.replaceChildren(...companies.map((c) => {
        const option = document.createElement('option'); option.value = c.slug; option.textContent = c.name; return option;
      }));
      companyPicker.classList.remove('hidden');
      showError(passwordError, 'Выберите пространство и нажмите «Войти» ещё раз.');
      return;
    }
    if (!result.ok) {
      showError(passwordError, 'Неверный email или пароль.');
      return;
    }
    window.location.href = loginDestination(location.search);
  });

  let pendingEmail = '';

  if (codeRequestForm instanceof HTMLFormElement && codeConfirmForm instanceof HTMLFormElement) {
    codeRequestForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const data = Object.fromEntries(new FormData(codeRequestForm).entries());
      pendingEmail = String(data.email ?? '');
      rememberLastLogin(pendingEmail);
      await postJson('/api/auth/request-code', { email: pendingEmail, purpose: 'login' });
      codeRequestForm.classList.add('hidden');
      codeConfirmForm.classList.remove('hidden');
    });

    const codeError = document.getElementById('login-code-error');
    codeConfirmForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      hideError(codeError);
      const code = String(new FormData(codeConfirmForm).get('code') ?? '');
      const result = await postJson('/api/auth/login-code', { email: pendingEmail, code });
      if (!result.ok) {
        showError(codeError, 'Неверный или истёкший код.');
        return;
      }
      window.location.href = loginDestination(location.search);
    });
  }

  const forgotLink = document.getElementById('forgot-password-link');
  const resetRequestForm = document.getElementById('reset-request-form');
  const resetConfirmForm = document.getElementById('reset-confirm-form');
  if (forgotLink && resetRequestForm instanceof HTMLFormElement && resetConfirmForm instanceof HTMLFormElement) {
    forgotLink.addEventListener('click', () => {
      passwordForm.classList.add('hidden');
      codeRequestForm?.classList.add('hidden');
      codeConfirmForm?.classList.add('hidden');
      resetRequestForm.classList.remove('hidden');
    });

    let resetEmail = '';
    resetRequestForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const data = Object.fromEntries(new FormData(resetRequestForm).entries());
      resetEmail = String(data.email ?? '');
      rememberLastLogin(resetEmail);
      await postJson('/api/auth/request-code', { email: resetEmail, purpose: 'password_reset' });
      resetRequestForm.classList.add('hidden');
      resetConfirmForm.classList.remove('hidden');
    });

    const resetError = document.getElementById('reset-confirm-error');
    const resetSuccess = document.getElementById('reset-confirm-success');
    resetConfirmForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      hideError(resetError);
      const data = Object.fromEntries(new FormData(resetConfirmForm).entries());
      const result = await postJson('/api/auth/password-reset/confirm', {
        email: resetEmail, code: String(data.code ?? ''), newPassword: String(data.newPassword ?? ''),
      });
      if (!result.ok) {
        showError(resetError, 'Неверный код или пароль слишком короткий.');
        return;
      }
      resetConfirmForm.classList.add('hidden');
      resetSuccess?.classList.remove('hidden');
      if (resetSuccess) resetSuccess.textContent = 'Пароль сохранён. Теперь можно войти.';
      window.setTimeout(() => {
        passwordForm.classList.remove('hidden');
        resetSuccess?.classList.add('hidden');
      }, 2000);
    });
  }
}

function showMailPortal(id: string, url: string | null | undefined): void {
  const link = document.getElementById(id);
  if (link instanceof HTMLAnchorElement && url) {
    link.href = url;
    link.classList.remove('hidden');
  }
}

async function initDashboard(): Promise<void> {
  const loading = document.getElementById('dashboard-loading');
  const content = document.getElementById('dashboard-content');
  if (!loading || !content) return;

  const res = await fetch('/api/me', { credentials: 'same-origin' });
  if (!res.ok) {
    window.location.href = '/login';
    return;
  }
  const me = (await res.json()) as { email: string; firstName: string; lastName: string | null; role?: 'owner' | 'employee'; profileComplete: boolean; mailPortalUrl?: string | null; mailboxUrl?: string | null; filesUrl?:string|null; filesPortalUrl?:string|null };

  if (me.role === 'employee') {
    loading.classList.add('hidden');content.classList.remove('hidden');
    content.innerHTML='<h1 class="text-2xl font-semibold">Рабочее пространство</h1><div class="dashboard-actions dashboard-employee-actions"><button id="employee-chat" class="dashboard-action bg-brand-600 text-white">Открыть чат</button><a id="employee-files" class="dashboard-action border border-brand-600 text-brand-600" href="#">Открыть файлы</a><a id="employee-mail" class="dashboard-action border border-brand-600 text-brand-600" href="/mail/open">Открыть почту</a></div><p id="employee-entry-error" role="status" class="mt-4"></p>';
    showMailPortal('employee-files',me.filesUrl);
    if(!me.filesUrl)document.getElementById('employee-files')?.remove();
    if(!me.mailboxUrl)document.getElementById('employee-mail')?.remove();
    document.getElementById('employee-chat')!.onclick=async()=>{const b=document.getElementById('employee-chat') as HTMLButtonElement;b.disabled=true;try{const r=await postJson('/api/chat/open',{});if(!r.ok)throw new Error();location.href=String(r.data.redirectUrl);}catch{document.getElementById('employee-entry-error')!.textContent='Чат сейчас недоступен. Попробуйте позже.';}finally{b.disabled=false;}};
    document.getElementById('logout-button')!.onclick=()=>{void postJson('/api/auth/logout',{}).then(()=>location.href='/login');};
    return;
  }

  initProfileSetup(me);void initOwnerAccess();
  showMailPortal('open-files-link',me.filesUrl);
  showMailPortal('open-mail-link', me.mailboxUrl);

  const userEl = document.getElementById('dashboard-user');
  if (userEl) userEl.textContent = `${fullName(me.firstName, me.lastName)} · ${me.email}`;

  const companyRes = await fetch('/api/company', { credentials: 'same-origin' });
  if (companyRes.ok) {
    const company = (await companyRes.json()) as { name: string; slug: string; billing_status: string; trial_ends_at: string | null };
    const nameEl = document.getElementById('dashboard-company-name');
    if (nameEl) nameEl.textContent = company.name;
    // Nothing about billing while there is nothing to bill: "пробный период до <дата>" promises a
    // paid period that does not exist yet, and the fallback printed the raw English status.
    const statusEl = document.getElementById('dashboard-company-status');
    if (statusEl) statusEl.textContent = '';
  }

  loading.classList.add('hidden');
  content.classList.remove('hidden');

  document.getElementById('logout-button')?.addEventListener('click', async () => {
    await postJson('/api/auth/logout', {});
    window.location.href = '/login';
  });

  const openChatError = document.getElementById('open-chat-error');
  document.getElementById('open-chat-button')?.addEventListener('click', async (event) => {
    const button = event.currentTarget as HTMLButtonElement;
    if (button.disabled) return;
    const label = document.getElementById('open-chat-label');
    hideError(openChatError);
    button.disabled = true;
    if (label) label.textContent = 'Открываем…';
    try {
      const result = await postJson('/api/chat/open', {});
      if (!result.ok || typeof result.data.redirectUrl !== 'string') {
        showError(openChatError, 'Чат сейчас недоступен. Попробуйте позже.');
        return;
      }
      window.location.href = result.data.redirectUrl;
    } catch {
      showError(openChatError, 'Не удалось открыть чат. Проверьте соединение и попробуйте ещё раз.');
    } finally {
      button.disabled = false;
      if (label) label.textContent = 'Открыть чат';
    }
  });

  initEmployeeSection();
}

interface Employee {
  id: string;
  role: 'owner' | 'employee';
  chat_enabled?:boolean;
  external_discoverable?:boolean;
  external_discovery_revision?:string;
  email: string;
  first_name: string;
  last_name: string | null;
  birthday: string | null;
  status: 'active' | 'blocked' | 'removed' | 'purged';
  removed_at: string | null;
  purge_after: string | null;
  blocked_at: string | null;
  last_login_at: string | null;
  department: string | null;
  department_ids?:string[];
  filesManageFileAccess?:boolean;
  filesManageFolderAccess?:boolean;
  filesAccessManagementRevision?:string;
}

const EDIT_EMPLOYEE_ERRORS: Record<string, string> = {
  invalid_email: 'Проверьте email.',
  invalid_first_name: 'Укажите имя.',
  invalid_last_name: 'Укажите фамилию.',
  invalid_birthday: 'Проверьте дату рождения.',
  invalid_department: 'Название отдела не длиннее 100 символов.',
  email_taken: 'Этот email уже используется в компании.',
};

function initEmployeeSection(): void {
  const addButton = document.getElementById('add-employee-button');
  const form = document.getElementById('add-employee-form');
  const cancelButton = document.getElementById('cancel-add-employee');
  const editForm = document.getElementById('edit-employee-form');
  const cancelEditButton = document.getElementById('cancel-edit-employee');
  const editErrorEl = document.getElementById('edit-employee-error');
  const editIdField = document.getElementById('edit-employee-id');
  const listEl = document.getElementById('employee-list');
  const empty = document.getElementById('employee-list-empty');
  const seatsLine = document.getElementById('employee-seats');
  const departmentFields = ['employee-department','edit-employee-department'].map(id => document.getElementById(id)).filter((field): field is HTMLSelectElement => field instanceof HTMLSelectElement);
  let companyDepartments: {id:string;name:string}[] = [];
  function departmentChoices(): void {
    const names = [...new Set([...companyDepartments.map(d => d.name), ...allEmployees.map(e => e.department).filter((name): name is string => Boolean(name)), ...departmentFields.map(field => field.value).filter(Boolean)])].sort((a,b) => a.localeCompare(b,'ru'));
    for (const field of departmentFields) {
      const selected = field.value;
      field.replaceChildren(...['',...names].map(name => { const option=document.createElement('option');option.value=name;option.textContent=name||'Без отдела';return option; }));
      field.value=selected;
    }
  }
  function employeeDepartments(employee:Employee){return [...new Set([...(employee.department_ids??[]).map(id=>companyDepartments.find(d=>d.id===id)?.name).filter((name):name is string=>Boolean(name)),...(employee.department?[employee.department]:[])])].join(', ');}
  async function loadDepartments(): Promise<void> {
    try {
      const response=await fetch('/api/departments',{credentials:'same-origin',signal:AbortSignal.timeout(10000)});
      if(!response.ok)throw new Error('departments_unavailable');
      const data=await response.json() as {departments:{id:string;name:string}[]};
      if(!Array.isArray(data.departments)||data.departments.some(d=>!d||typeof d.id!=='string'||typeof d.name!=='string'))throw new Error('departments_unavailable');
      companyDepartments=data.departments;departmentChoices();
      document.querySelectorAll<HTMLElement>('[data-department-status]').forEach(el=>{el.textContent='';el.classList.add('hidden');});
    } catch {
      document.querySelectorAll<HTMLElement>('[data-department-status]').forEach(el=>{el.textContent='Не удалось обновить список отделов. Обновите страницу, чтобы выбрать новый отдел.';el.classList.remove('hidden');});
    }
  }
  const searchInput = document.getElementById('employee-search') as HTMLInputElement | null;
  const searchCount = document.getElementById('employee-search-count');
  let allEmployees: Employee[] = [];
  const passwordBox = document.getElementById('new-employee-password');
  const errorEl = document.getElementById('add-employee-error');
  if (!(form instanceof HTMLFormElement) || !(editForm instanceof HTMLFormElement) || !(editIdField instanceof HTMLInputElement) || !listEl) return;
  const list: HTMLElement = listEl;
  const addForm: HTMLFormElement = form;
  const employeeMail=initEmployeeMail(addForm,passwordBox,()=>renderEmployees());
  const filesAccessEditor=initFilesAccessEditor(async()=>{await loadEmployees(true);return allEmployees;});
  const discovery=initEmployeeDiscoverability();
  let creationAttempt=crypto.randomUUID(),creatingEmployee=false;
  const employeeEditForm: HTMLFormElement = editForm;
  const employeeEditIdField: HTMLInputElement = editIdField;
  const editorPanel = document.getElementById('employee-editor-panel');
  const editorEmpty = document.getElementById('employee-editor-empty');
  const editorDialog = document.getElementById('employee-editor-dialog') as HTMLDialogElement;
  const mobileEditor = document.getElementById('employee-editor-mobile');
  const wideEditor = window.matchMedia('(min-width: 1024px)');
  let editingId: string | null = null;
  let savingEdit = false;
  let originalEdit = '';
  let originalDepartment = '';
  const editValues = (): string => JSON.stringify(Array.from(new FormData(employeeEditForm).entries()));

  function placeEditor(): void {
    if (wideEditor.matches) {
      if (editorDialog.open) editorDialog.close();
      editorPanel?.append(employeeEditForm,employeeMail.editor.element,filesAccessEditor.element);
    } else {
      mobileEditor?.append(employeeEditForm,employeeMail.editor.element,filesAccessEditor.element);
      if (editingId && !editorDialog.open) editorDialog.showModal();
    }
  }
  wideEditor.addEventListener('change', placeEditor);

  function markSelection(): void {
    for (const row of list.querySelectorAll<HTMLElement>('li[data-employee-id]')) {
      row.dataset.selected = String(row.dataset.employeeId === editingId);
      row.querySelector('[data-edit-employee]')?.setAttribute('aria-expanded', String(row.dataset.employeeId === editingId));
    }
  }

  function closeEditor(): void {
    employeeEditForm.classList.add('hidden');
    employeeMail.editor.close();
    filesAccessEditor.close();
    discovery.close();
    editingId = null;
    if (editorDialog.open) editorDialog.close();
    editorEmpty?.classList.remove('hidden');
    employeeEditForm.reset();
    markSelection();
  }
  editorDialog.addEventListener('cancel', event => {
    event.preventDefault();
    if (savingEdit||filesAccessEditor.busy()) return;
    const id = editingId;
    closeEditor();
    if (id) returnToEmployee(id);
  });

  function returnToEmployee(id: string): void {
    const button = Array.from(list.querySelectorAll<HTMLButtonElement>('button[data-edit-employee]'))
      .find(button => button.dataset.editEmployee === id);
    button?.focus({ preventScroll: true });
  }

  addButton?.addEventListener('click', () => {
    if (savingEdit||filesAccessEditor.busy()) return;
    if (editingId && (editValues() !== originalEdit||filesAccessEditor.dirty()) && !confirm('Перейти к добавлению сотрудника без сохранения изменений?')) return;
    closeEditor();
    addForm.classList.remove('hidden');
    document.getElementById('employee-first-name')?.focus();
  });
  cancelButton?.addEventListener('click', () => {
    addForm.classList.add('hidden');
    addForm.reset();
    creationAttempt=crypto.randomUUID();
  });

  function openEditForm(employee: Employee): void {
    if (savingEdit||filesAccessEditor.busy()) return;
    if (editingId && (editValues() !== originalEdit||filesAccessEditor.dirty()) && !confirm('Открыть другого участника без сохранения изменений?')) return;
    editingId = employee.id;
    addForm.classList.add('hidden');
    hideError(editErrorEl);
    employeeEditIdField.value = employee.id;
    (employeeEditForm.elements.namedItem('firstName') as HTMLInputElement).value = employee.first_name;
    (employeeEditForm.elements.namedItem('lastName') as HTMLInputElement).value = employee.last_name ?? '';
    (employeeEditForm.elements.namedItem('email') as HTMLInputElement).value = employee.email;
    (employeeEditForm.elements.namedItem('birthday') as HTMLInputElement).value = employee.birthday ?? '';
    originalDepartment = employee.department ?? '';
    departmentChoices();
    (employeeEditForm.elements.namedItem('department') as HTMLSelectElement).value = originalDepartment;
    void loadDepartments();
    // The owner's address is their login and the verified mailbox password recovery goes to, so
    // the server refuses to change it here; disable the field rather than silently ignore typing.
    const emailField = employeeEditForm.elements.namedItem('email') as HTMLInputElement;
    const emailHint = document.getElementById('edit-employee-email-hint');
    // readOnly, not disabled: a disabled field is left out of FormData entirely, so the owner's
    // address would arrive empty and the server would reject the save as an invalid email.
    emailField.readOnly = employee.role === 'owner';
    emailHint?.classList.toggle('hidden', employee.role !== 'owner');
    employeeEditForm.classList.remove('hidden');
    const title = document.getElementById('edit-employee-title');
    if (title) title.textContent = `№ ${allEmployees.findIndex(e => e.id === employee.id) + 1} · ${fullName(employee.first_name, employee.last_name)}`;
    originalEdit = editValues();
    employeeMail.editor.open(employee);
    filesAccessEditor.open(employee);
    discovery.open(employee);
    editorEmpty?.classList.add('hidden');
    placeEditor();
    markSelection();
    title?.focus({ preventScroll: true });
  }

  cancelEditButton?.addEventListener('click', () => {
    const id = employeeEditIdField.value;
    if (savingEdit||filesAccessEditor.busy()) return;
    closeEditor();
    returnToEmployee(id);
  });

  employeeEditForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (savingEdit||filesAccessEditor.busy()) return;
    hideError(editErrorEl);
    const formData = new FormData(employeeEditForm);
    const body = {
      firstName: String(formData.get('firstName') ?? ''),
      lastName: String(formData.get('lastName') ?? ''),
      email: String(formData.get('email') ?? ''),
      birthday: String(formData.get('birthday') ?? '') || null,
      ...(String(formData.get('department') ?? '') === originalDepartment ? {} : {department: String(formData.get('department') ?? '') || null}),
    };
    const editedId = employeeEditIdField.value;
    const submit = employeeEditForm.querySelector<HTMLButtonElement>('button[type=submit]');
    savingEdit = true;
    if (submit) { submit.disabled = true; submit.textContent = 'Сохраняем…'; }
    try {
      if(!await filesAccessEditor.saveIfDirty())return;
      const result = await patchJson(`/api/employees/${editedId}`, body);
      if (!result.ok) {
        const code = String(result.data.error ?? '');
        showError(editErrorEl, EDIT_EMPLOYEE_ERRORS[code] ?? 'Не удалось сохранить изменения.');
        return;
      }
      closeEditor();
      await loadEmployees();
      returnToEmployee(editedId);
    } catch { showError(editErrorEl, 'Не удалось сохранить изменения. Проверьте соединение и повторите.'); }
    finally {
      savingEdit = false;
      if (submit) { submit.disabled = false; submit.textContent = 'Сохранить'; }
    }
  });

  const importButton = document.getElementById('import-csv-button');
  const importFile = document.getElementById('import-csv-file') as HTMLInputElement | null;
  const importInvite = document.getElementById('import-csv-invite') as HTMLInputElement | null;
  const importResult = document.getElementById('import-csv-result');

  const UPLOAD_ERRORS: Record<string, string> = {
    empty_file: 'Файл пустой.',
    no_rows: 'В файле нет строк с сотрудниками.',
    no_email_column: 'В заголовке файла нет колонки email.',
    file_too_large: 'Файл слишком большой.',
    too_many_rows: 'Больше 500 строк за раз — разделите файл.',
  };

  const IMPORT_ERRORS: Record<string, string> = {
    invalid_email: 'неверная почта',
    invalid_first_name: 'не указано имя',
    invalid_last_name: 'не указана фамилия',
    invalid_birthday: 'неверная дата рождения',
    invalid_department: 'слишком длинное название отдела',
    duplicate_in_file: 'адрес встречается в файле дважды',
    not_an_employee: 'это владелец, он меняется отдельно',
    email_taken: 'адрес уже занят',
    employee_limit_reached: 'достигнут лимит сотрудников',
    row_failed: 'строку не удалось обработать',
    create_failed: 'строку не удалось обработать',
  };

  searchInput?.addEventListener('input', () => renderEmployees());

  importButton?.addEventListener('click', () => importFile?.click());
  importFile?.addEventListener('change', async () => {
    const file = importFile.files?.[0];
    if (!file || !importResult) return;
    importFile.value = '';   // so re-picking the same file after a fix still fires 'change'
    importResult.className = 'mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600';
    importResult.textContent = 'Загружаю…';

    const res = await fetch('/api/employees/import', {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ csv: await file.text(), sendInviteEmail: importInvite?.checked !== false }),
    });
    const data = (await res.json().catch(() => ({}))) as
      { created?: number; updated?: number; failed?: { line: number; email: string; error: string }[]; error?: string };

    if (!res.ok) {
      importResult.className = 'mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700';
      importResult.textContent = UPLOAD_ERRORS[data.error ?? ''] ?? 'Не удалось загрузить файл.';
      return;
    }

    const failed = data.failed ?? [];
    importResult.className = failed.length > 0
      ? 'mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'
      : 'mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800';
    importResult.textContent = `Добавлено: ${data.created ?? 0}. Обновлено: ${data.updated ?? 0}.`;
    if (failed.length > 0) {
      // Naming the line and the reason matters: a 200-row upload with three bad dates is
      // otherwise a hunt through the file.
      const details = document.createElement('ul');
      details.className = 'mt-2 list-disc pl-5';
      for (const row of failed.slice(0, 20)) {
        const li = document.createElement('li');
        li.textContent = `строка ${row.line}${row.email ? ` (${row.email})` : ''}: ${IMPORT_ERRORS[row.error] ?? row.error}`;
        details.append(li);
      }
      if (failed.length > 20) {
        const more = document.createElement('li');
        more.textContent = `…и ещё ${failed.length - 20}`;
        details.append(more);
      }
      importResult.append(details);
    }
    await loadEmployees();
  });

  async function loadEmployees(requireFresh=false): Promise<void> {
    const res = await fetch('/api/employees', { credentials: 'same-origin' });
    if (!res.ok) {if(requireFresh)throw Error('Employee readback failed');return;}
    const data = (await res.json()) as { employees: Employee[]; seats?: { used: number; limit: number | null } };
    if(requireFresh&&!Array.isArray(data.employees))throw Error('Invalid employee readback');
    if (seatsLine) {
      const seats = data.seats;
      seatsLine.textContent = !seats ? ''
        : seats.limit === null ? `Сотрудников: ${seats.used}`
        : `Сотрудников: ${seats.used} из ${seats.limit}`;
      // Worth a nudge before the limit is hit, not after a half-finished upload.
      seatsLine.className = seats && seats.limit !== null && seats.used >= seats.limit
        ? 'mt-1 text-sm font-medium text-amber-700'
        : 'mt-1 text-sm text-slate-500';
    }
    await employeeMail.refresh();
    allEmployees = data.employees;
    departmentChoices();
    await loadDepartments();
    renderEmployees();
  }

  // Filter the list already loaded for this view without a request for every keystroke.
  function matches(employee: Employee, query: string): boolean {
    if (query === '') return true;
    const haystack = [
      employee.first_name, employee.last_name ?? '', employee.email, employeeDepartments(employee),...employeeMail.addresses(employee.id),
    ].join(' ').toLowerCase();
    // Every word must appear somewhere, so "иван прод" finds Иванов in Продажах.
    return query.split(/\s+/).filter(Boolean).every(word => haystack.includes(word));
  }

  function renderEmployees(): void {
    const query = (searchInput?.value ?? '').trim().toLowerCase();
    const numbers = new Map(allEmployees.map((e, i) => [e.id, i + 1]));
    const shown = allEmployees.filter(e => matches(e, query) || (query !== '' && query.replace(/^№\s*/, '') === String(numbers.get(e.id))));

    if (searchCount) {
      const text = query === '' ? '' : `Найдено: ${shown.length} из ${allEmployees.length}`;
      searchCount.textContent = text;
      searchCount.classList.toggle('hidden', text === '');
    }

    const scrollPosition = list.scrollTop;
    list.innerHTML = '';
    if (allEmployees.length === 0) {
      empty?.classList.remove('hidden');
      return;
    }
    empty?.classList.add('hidden');
    if (shown.length === 0) {
      // Silence here reads as a broken list rather than an empty result.
      const none = document.createElement('li');
      none.className = 'px-4 py-3 text-sm text-slate-500';
      none.textContent = 'Никто не найден.';
      list.append(none);
      return;
    }
    for (const employee of shown) {
      const item = document.createElement('li');
      item.dataset.employeeId = employee.id;
      item.className = 'flex flex-col items-start justify-between gap-3 px-4 py-3 sm:flex-row sm:items-center';

      const info = document.createElement('div');
      info.className = 'min-w-0 max-w-full break-words';
      const nameLine = document.createElement('p');
      nameLine.className = 'text-sm font-medium text-slate-900';
      const number = document.createElement('span');
      number.className = 'mr-2 font-mono text-xs text-slate-500';
      number.dataset.employeeNumber = String(numbers.get(employee.id));
      number.textContent = `№ ${numbers.get(employee.id)}`;
      nameLine.append(number, document.createTextNode(fullName(employee.first_name, employee.last_name)));
      if (employee.role === 'owner') {
        const badge = document.createElement('span');
        badge.className = 'ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600';
        badge.textContent = employee.chat_enabled===false?'Служебный владелец':'Владелец';
        nameLine.append(badge);
      }
      const emailLine = document.createElement('p');
      emailLine.className = 'text-sm text-slate-500';
      emailLine.textContent = employee.email;
      info.append(nameLine, emailLine);
      employeeMail.roster(info,employee.id);
      if (employee.birthday) {
        const birthdayLine = document.createElement('p');
        birthdayLine.className = 'text-sm text-slate-500';
        birthdayLine.textContent = `Дата рождения: ${formatDateOnly(employee.birthday)}`;
        info.append(birthdayLine);
      }
      if (employeeDepartments(employee)) {
        const departmentLine = document.createElement('p');
        departmentLine.className = 'text-sm text-slate-500';
        departmentLine.textContent = `Отделы: ${employeeDepartments(employee)}`;
        info.append(departmentLine);
      }
      const loginLine = document.createElement('p');
      loginLine.className = 'text-sm text-slate-500';
      loginLine.textContent = `Последний вход: ${formatTimestamp(employee.last_login_at)}`;
      info.append(loginLine);
      if (employee.status === 'blocked') {
        const blockedLine = document.createElement('p');
        blockedLine.className = 'text-xs font-medium text-amber-700';
        blockedLine.textContent = employee.blocked_at
          ? `Заблокирован ${new Date(employee.blocked_at).toLocaleDateString('ru-RU')} — вход и чат закрыты`
          : 'Заблокирован — вход и чат закрыты';
        info.append(blockedLine);
      }
      if (employee.status === 'removed' && employee.purge_after) {
        const removedLine = document.createElement('p');
        removedLine.className = 'text-xs text-amber-700';
        removedLine.textContent = `Удалён, можно восстановить до ${new Date(employee.purge_after).toLocaleDateString('ru-RU')}`;
        info.append(removedLine);
      }

      item.append(info);

      const actions = document.createElement('div');
      actions.className = 'flex shrink-0 flex-wrap items-center gap-1';

      employeeMail.action(actions,employee);

      // The owner gets Edit but not Delete: they are the account running this page, and the
      // company would be left with nobody able to administer it.
      // Never for the owner: the server refuses to block them anyway (the account would lock
      // itself out of its own company), so showing the button would only produce an error.
      if (employee.role === 'employee' && employee.status === 'blocked') {
        const unblock = document.createElement('button');
        unblock.textContent = 'Разблокировать';
        unblock.className = 'rounded-lg px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50';
        unblock.addEventListener('click', async () => {
          const res = await postJson(`/api/employees/${employee.id}/unblock`, {});
          if (!res.ok) {
            alert(res.data.error === 'employee_limit_reached'
              ? 'Нет свободных мест: заблокированные не занимают место, а все места сейчас заняты.'
              : 'Не удалось разблокировать.');
            return;
          }
          await loadEmployees();
        });
        actions.append(unblock);
      }

      if (employee.role === 'employee' && employee.status === 'active') {
        const blockAction = document.createElement('button');
        blockAction.textContent = 'Заблокировать';
        blockAction.className = 'rounded-lg px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50';
        blockAction.addEventListener('click', async () => {
          if (!confirm(`Заблокировать ${fullName(employee.first_name, employee.last_name)}? Вход и чат закроются сразу. Данные сохранятся, разблокировать можно в любой момент.`)) return;
          const res = await postJson(`/api/employees/${employee.id}/block`, {});
          if (!res.ok) { alert('Не удалось заблокировать.'); return; }
          await loadEmployees();
        });
        actions.append(blockAction);
      }

      if (employee.status === 'active') {
        const editAction = document.createElement('button');
        editAction.textContent = 'Редактировать';
        editAction.dataset.editEmployee = employee.id;
        editAction.setAttribute('aria-controls', 'edit-employee-form');
        editAction.className = 'rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100';
        editAction.addEventListener('click', () => openEditForm(employee));
        actions.append(editAction);
      }

      if (employee.role === 'owner') {
        item.append(actions);
        list.append(item);
        continue;
      }

      const action = document.createElement('button');
      action.className = 'shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium';
      if (employee.status === 'active') {
        action.textContent = 'Удалить';
        action.className += ' text-red-600 hover:bg-red-50';
        action.addEventListener('click', async () => {
          if (!confirm(`Удалить ${fullName(employee.first_name, employee.last_name)} из чата? Восстановить можно в течение 30 дней.`)) return;
          await fetch(`/api/employees/${employee.id}`, { method: 'DELETE', credentials: 'same-origin' });
          await loadEmployees();
        });
      } else {
        action.textContent = 'Восстановить';
        action.className += ' text-brand-600 hover:bg-brand-50';
        action.addEventListener('click', async () => {
          await fetch(`/api/employees/${employee.id}/restore`, { method: 'POST', credentials: 'same-origin' });
          await loadEmployees();
        });
      }
      actions.append(action);

      item.append(actions);
      list.append(item);
    }
    list.scrollTop = scrollPosition;
    markSelection();
  }

  addForm.addEventListener('submit', async (event) => {
    event.preventDefault();if(creatingEmployee)return;
    hideError(errorEl);creatingEmployee=true;
    const submit=addForm.querySelector<HTMLButtonElement>('button[type=submit]')!;submit.disabled=true;submit.textContent='Создаём…';
    const formData=new FormData(addForm);
    const body={firstName:String(formData.get('firstName')??''),lastName:String(formData.get('lastName')??''),email:String(formData.get('email')??''),birthday:String(formData.get('birthday')??'')||null,department:String(formData.get('department')??'')||null,sendInviteEmail:formData.get('sendInviteEmail')==='on',accessEmail:String(formData.get('accessEmail')??''),attemptId:creationAttempt};
    try{
      const result=await postJson('/api/employees',body);
      if(!result.ok){const code=String(result.data.error??'');showError(errorEl,employeeMailErrors[code]??EDIT_EMPLOYEE_ERRORS[code]??(code==='employee_limit_reached'?'Достигнут лимит сотрудников.':'Не удалось добавить сотрудника. Повторите действие.'));return;}
      addForm.classList.add('hidden');addForm.reset();creationAttempt=crypto.randomUUID();employeeMail.show(body.email,result.data);await loadEmployees();
    }catch{showError(errorEl,'Соединение прервалось. Повторите действие с теми же данными: уже созданный сотрудник не будет продублирован.');}
    finally{creatingEmployee=false;submit.disabled=false;submit.textContent='Создать';}
  });

  void loadEmployees();
}

function initSuperAdminLoginForm(): void {
  const form = document.getElementById('super-admin-login-form');
  if (!(form instanceof HTMLFormElement)) return;
  const errorEl = document.getElementById('super-admin-login-error');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    hideError(errorEl);
    const data = Object.fromEntries(new FormData(form).entries());
    const result = await postJson('/api/super-admin/login', data);
    if (!result.ok) {
      showError(errorEl, 'Неверный email или пароль.');
      return;
    }
    window.location.href = '/super-admin';
  });
}

interface CompanyOverview {
  verified_at: string | null;
  id: string;
  slug: string;
  name: string;
  billing_status: string;
  trial_ends_at: string | null;
  created_at: string;
  owner_count: string;
  active_employee_count: string;
  removed_employee_count: string;
}

interface SuperAdminAccount {
  id: string;
  role: 'owner' | 'employee';
  email: string;
  first_name: string;
  last_name: string | null;
  status: 'active' | 'blocked' | 'removed' | 'purged';
  created_at: string;
}

interface BugReport {
  source?: 'chat' | 'mail' | 'files';
  address?: string;
  context?: {view?:string;viewport?:string;userAgent?:string;language?:string};
  id: string;
  number: number;
  title: string;
  description: string;
  status: string;
  urgent: boolean;
  brief?: string | null;
  security_flag?: boolean;
  fix_approved?: boolean;
  fix_approved_by?: string | null;
  fix_approved_at?: string | null;
  created_at: string;
}

const BUG_STATUS_LABELS: Record<string, string> = {
  new: 'новый',
  enhancement: 'доработка',
  // "закрыт" said nothing about whether the thing was fixed or thrown away, and "ждёт проверки"
  // sent the owner to production looking for a fix that was still sitting in a branch. Both now
  // say what actually happened to the report.
  done: 'выложено на прод',
  rejected: 'отклонён агентом',
  in_progress: 'в работе',
  waiting_check: 'исправление готово, ждёт выкладки',
  needs_rework: 'на доработку',
  needs_human: 'ждёт решения человека',
};

async function loadBugQueue(requireChat = false, render = true): Promise<BugReport[] | null> {
  const list = document.getElementById('bug-queue-list');
  const note = document.getElementById('bug-queue-note');
  const emptyEl = document.getElementById('bug-queue-empty');
  if (!list) return null;

  let res: Response;
  let parsed: unknown;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 20_000);
  try {
    res = await fetch('/api/super-admin/bug-reports', { credentials: 'same-origin', signal: controller.signal });
    parsed = await res.json();
  } catch {
    if (note) { note.textContent = 'Не удалось загрузить обращения. Список сохранён; попробуйте обновить его.'; note.classList.remove('hidden'); }
    return null;
  } finally { clearTimeout(timer); }
  const data = (parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}) as { reports?: BugReport[]; unavailable?: string; mailUnavailable?:boolean; mailNext?:string|null;filesUnavailable?:boolean;filesNext?:string|null };

  if (note) {
    // Say which of the two it is: a missing token is a setup step, an unreachable chat is an
    // outage. Showing an empty list for either would be a lie.
    let text = !res.ok ? 'Не удалось загрузить обращения. Обновите страницу или войдите снова.' : data.unavailable === 'not_configured' ? 'Обращения из чата не подключены.'
      : data.unavailable === 'chat_unreachable' ? 'Не удалось получить обращения из чата: чат не отвечает.'
      : '';
    if(data.mailUnavailable)text+=(text?' ':'')+'Не удалось получить обращения из почты. Повторите позже.';
    if(data.filesUnavailable)text+=(text?' ':'')+'Не удалось получить обращения из файлов. Повторите позже.';
    note.textContent = text;
    note.classList.toggle('hidden', text === '');
  }

  if (!res.ok || (requireChat && data.unavailable)) return null;
  if (!Array.isArray(data.reports)) {
    if (note) { note.textContent = 'Не удалось прочитать список обращений. Проверьте статус позже.'; note.classList.remove('hidden'); }
    return null;
  }
  const reports = data.reports;
  if (!render) return reports;
  // Three groups, not two. Shipped and rejected are opposite outcomes and lumping them together as
  // "closed" hid the good news: after seven fixes went live the owner could no longer see a single
  // one of them, and said so.
  const open = reports.filter(r => r.status !== 'done' && r.status !== 'rejected');
  const shipped = reports.filter(r => r.status === 'done');
  const rejected = reports.filter(r => r.status === 'rejected');

  emptyEl?.classList.toggle('hidden', reports.length > 0 || Boolean(data.unavailable||data.mailUnavailable||data.filesUnavailable||!res.ok));
  list.replaceChildren(...open.map(report => renderBugReport(report)));
  for(const source of ['mail','files'] as const){
    const next=source==='mail'?data.mailNext:data.filesNext;if(!next)continue;
    let cursor=next;
    const label=source==='mail'?'Ещё обращения из почты':'Ещё обращения из файлов';
    const more=document.createElement('button');more.className='rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300';more.textContent=label;list.append(more);
    more.onclick=async()=>{
      more.disabled=true;
      try{
        const r=await fetch(`/api/super-admin/${source}/bug-reports?before=${encodeURIComponent(cursor)}`);
        if(!r.ok)throw new Error('unavailable');
        const page=await r.json() as {reports:BugReport[];next:string|null};
        if(!more.isConnected)return;
        for(const report of page.reports){
          if(report.status==='done'&&shippedList&&shippedTitle){
            shipped.push(report);shippedList.append(renderBugReport(report));
            shippedTitle.textContent=`Выложено на прод (${shipped.length})`;shippedTitle.classList.remove('hidden');
          }else more.before(renderBugReport(report));
        }
        if(page.next){cursor=page.next;more.textContent=label;}else more.remove();
      }catch{more.textContent='Не удалось загрузить — повторить';}finally{more.disabled=false;}
    };
  }

  // Shipped: visible by default, because "what reached production" is the question this panel is
  // opened to answer.
  const shippedList = document.getElementById('bug-queue-shipped');
  const shippedTitle = document.getElementById('bug-queue-shipped-title');
  if (shippedList && shippedTitle) {
    shippedTitle.textContent = `Выложено на прод (${shipped.length})`;
    shippedTitle.classList.toggle('hidden', shipped.length === 0);
    shippedList.replaceChildren(...shipped.map(report => renderBugReport(report)));
  }

  // Rejected stays folded away, and stays reachable: an agent deciding a user's report is "not a
  // bug" is exactly the call a person may want to overrule, and it is invisible otherwise.
  const closedList = document.getElementById('bug-queue-closed');
  const toggle = document.getElementById('bug-queue-closed-toggle');
  if (closedList && toggle) {
    toggle.textContent = rejected.length === 0 ? '' : `Отклонённые (${rejected.length})`;
    toggle.classList.toggle('hidden', rejected.length === 0);
    closedList.replaceChildren(...rejected.map(report => renderBugReport(report)));
    closedList.classList.add('hidden');
    toggle.onclick = () => {
      const nowHidden = closedList.classList.toggle('hidden');
      toggle.textContent = `${nowHidden ? 'Отклонённые' : 'Скрыть отклонённые'} (${rejected.length})`;
    };
  }
  return reports;
}

function showBugDecision(message: string, kind: 'confirmed' | 'rejected' | 'uncertain'): void {
  const feedback = document.getElementById('bug-queue-action-result');
  if (!feedback) return;
  feedback.className = 'sticky top-2 z-10 mt-3 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm ' + (kind === 'confirmed' ? 'text-emerald-300' : kind === 'rejected' ? 'text-red-300' : 'text-amber-300');
  feedback.textContent = message;
}

const bugDecisionLocks = new Map<string, {before: BugReport; action: 'approve' | 'retriage'; approved: boolean}>();

function bindBugDecision(button: HTMLButtonElement, inline: HTMLElement, report: BugReport, action: 'approve' | 'retriage', approved: boolean): void {
  const pending = bugDecisionLocks.get(report.id);
  if (pending && bugDecisionReadConfirms(pending.before, pending.action, pending.approved, report)) bugDecisionLocks.delete(report.id);
  else if (pending) {
    button.disabled = true;
    inline.textContent = 'Результат предыдущего действия ещё не подтверждён. Обновите список для проверки.';
  }
  button.addEventListener('click', async () => {
    if (button.disabled || bugDecisionLocks.has(report.id)) return;
    bugDecisionLocks.set(report.id, {before: report, action, approved});
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Сохраняю…';
    inline.textContent = 'Сохраняю решение…';
    showBugDecision(`#${report.number} — сохраняю решение…`, 'uncertain');
    const outcome = await runBugDecision({
      before: report, action, approved,
      send: async () => {
        try { return await sendBugDecision(report.id, action, approved); }
        finally {
          button.textContent = 'Проверяю…';
          inline.textContent = 'Проверяю статус обращения…';
          showBugDecision(`#${report.number} — проверяю статус обращения…`, 'uncertain');
        }
      },
      read: async () => {
        const reports = await loadBugQueue(true, false);
        return reports?.find(row => row.id === report.id && (!row.source || row.source === 'chat')) ?? null;
      },
    });
    if (outcome.kind !== 'uncertain') bugDecisionLocks.delete(report.id);
    inline.className = 'ml-3 text-sm ' + (outcome.kind === 'confirmed' ? 'text-emerald-400' : 'text-amber-300');
    inline.textContent = outcome.message;
    showBugDecision(`#${report.number} — ${outcome.message}`, outcome.kind);
    button.textContent = label;
    button.disabled = !outcome.retryAllowed;
  });
}

function renderBugReport(report: BugReport): HTMLElement {
  const card = document.createElement('div');
  const external=report.source==='mail'||report.source==='files';
  card.className = 'rounded-lg border border-slate-800 bg-slate-900 p-4'+(external?` ${report.source}-bug-card`:'');

  const head = document.createElement('div');
  head.className = 'flex items-start justify-between gap-4';

  const title = document.createElement('p');
  title.className = 'text-sm font-medium text-slate-100';
  title.textContent = `${report.source==='mail'?'Почта ':report.source==='files'?'Файлы ':''}#${report.number} — ${report.title}`;

  const badges = document.createElement('div');
  badges.className = 'flex shrink-0 items-center gap-2';
  const status = document.createElement('span');
  // Colour carries the outcome at a glance: green for live, red for thrown out, grey for still
  // moving. Reading a row should not require reading the word.
  const tone = report.status === 'done' ? 'bg-emerald-900 text-emerald-200'
    : report.status === 'rejected' ? 'bg-rose-950 text-rose-300'
      : 'bg-slate-800 text-slate-300';
  status.className = `rounded-full px-2 py-0.5 text-xs ${tone}`;
  status.textContent = BUG_STATUS_LABELS[report.status] ?? report.status;
  badges.append(status);
  if (report.urgent) {
    const urgent = document.createElement('span');
    urgent.className = 'rounded-full bg-amber-900 px-2 py-0.5 text-xs text-amber-200';
    urgent.textContent = 'срочно';
    badges.append(urgent);
  }
  if (report.security_flag) {
    const flag = document.createElement('span');
    flag.className = 'rounded-full bg-red-900 px-2 py-0.5 text-xs text-red-200';
    flag.textContent = 'подозрительный';
    badges.append(flag);
  }
  head.append(title, badges);

  const body = document.createElement('p');
  body.className = 'mt-2 whitespace-pre-wrap text-sm text-slate-400';
  body.textContent = !external && report.description.length > 600 ? `${report.description.slice(0, 600)}…` : report.description;

  const meta = document.createElement('p');
  meta.className = 'mt-2 text-xs text-slate-500';
  meta.textContent = `Создан ${new Date(report.created_at).toLocaleString('ru-RU')}`;

  card.append(head, body, meta);
  if(external){
    if(report.address){const sender=document.createElement('p');sender.className='mt-2 text-xs text-slate-400';sender.textContent=`От: ${report.address}`;card.append(sender);}
    if(report.context){const details=document.createElement('details');details.className='mt-2 text-xs text-slate-500';const label=document.createElement('summary');label.textContent='Технические сведения';const text=document.createElement('p');text.className='mt-2 break-words';text.textContent=[report.context.view,report.context.viewport,report.context.language,report.context.userAgent].filter(Boolean).join(' · ');details.append(label,text);card.append(details);}
    return card;
  }

  // Approval is offered only where it means something: a report parked for a person, not already
  // approved, and not flagged as suspicious -- those must stay a deliberate manual decision.
  const approvable = report.status === 'needs_human' || report.status === 'enhancement';
  // Chat consumes the active approval when claiming a fix, but retains its date.
  // Keep that decision visible independently of the current queue permission.
  const previousApproval = typeof report.fix_approved_at === 'string'
    && Number.isFinite(Date.parse(report.fix_approved_at));
  if (previousApproval) {
    const history = document.createElement('p');
    history.className = 'mt-3 text-sm text-slate-300';
    history.textContent = `${report.fix_approved ? 'Одобрено' : 'Ранее одобрено'} ${new Date(report.fix_approved_at!).toLocaleString('ru-RU')}.`;
    if (!report.fix_approved && approvable) {
      history.textContent += ' Предыдущая попытка вернулась на разбор. Одобрение сохранено в истории; новая попытка требует повторного одобрения.';
    }
    card.append(history);
  }

  // The plan is shown before the button, so a person approves a specific piece of work rather
  // than a title -- for an enhancement especially, "approve" would otherwise mean "build
  // whatever you decide this means".
  if (report.brief && approvable) {
    const brief = document.createElement('div');
    brief.className = 'mt-3 rounded-lg border border-slate-700 bg-slate-950 p-3';
    const label = document.createElement('p');
    label.className = 'text-xs font-medium uppercase tracking-wide text-slate-500';
    label.textContent = report.status === 'enhancement' ? 'Что будет сделано' : 'Что будет исправлено';
    const text = document.createElement('p');
    text.className = 'mt-1 whitespace-pre-wrap text-sm text-slate-300';
    text.textContent = report.brief;
    brief.append(label, text);
    card.append(brief);
  }

  if (approvable && !report.fix_approved && !report.security_flag) {
    const approve = document.createElement('button');
    approve.className = 'mt-3 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700';
    approve.textContent = previousApproval ? 'Одобрить повторную попытку'
      : report.status === 'enhancement' ? 'Одобрить доработку' : 'Одобрить исправление';
    const result = document.createElement('span');
    result.className = 'ml-3 text-sm';
    bindBugDecision(approve, result, report, 'approve', true);
    card.append(approve, result);
  } else if (report.fix_approved) {
    const approved = document.createElement('p');
    approved.className = 'mt-3 text-sm text-emerald-400';
    approved.textContent = !approvable ? 'Одобрение принято. Текущее состояние работы указано в статусе обращения.' : report.fix_approved_by
      ? `Исправление одобрено (${report.fix_approved_by}), ждёт агента.`
      : 'Исправление одобрено, ждёт агента.';
    card.append(approved);
    // Revocable only until the agent starts; chat answers 409 after that, which is reported
    // honestly rather than shown as success.
    if (approvable) {
      const revoke = document.createElement('button');
      revoke.className = 'mt-2 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-800';
      revoke.textContent = 'Отозвать одобрение';
      const outcome = document.createElement('span');
      outcome.className = 'ml-3 text-sm';
      bindBugDecision(revoke, outcome, report, 'approve', false);
      card.append(document.createElement('br'), revoke, outcome);
    }
  } else if (report.security_flag) {
    const blocked = document.createElement('p');
    blocked.className = 'mt-3 text-sm text-red-400';
    blocked.textContent = 'Помечен как подозрительный — автоматическое исправление недоступно, нужен разбор вручную.';
    card.append(blocked);
  } else {
    // Without this the card just has no button and the reason is invisible -- the panel should
    // say why an action is unavailable instead of leaving it to be guessed.
    const why = document.createElement('p');
    why.className = 'mt-3 text-sm text-slate-500';
    why.textContent = report.status === 'new'
      ? 'Ждёт разбора агентом. Одобрять пока нечего: агент ещё не решил, что это ошибка, и не предложил исправление.'
      : report.status === 'in_progress' ? 'Агент уже работает над отчётом.'
      : report.status === 'waiting_check' ? 'Исправление готово и ждёт проверки человеком.'
      : report.status === 'needs_rework' ? 'Исправление отправлено на доработку, агент вернётся к нему сам.'
      : report.status === 'rejected' ? 'Агент решил, что это не ошибка и не доработка.'
      : report.status === 'done' ? 'Отчёт закрыт.'
      : 'Действий по этому отчёту сейчас нет.';
    card.append(why);

    // A person can overrule the agent: back to triage, which re-reads it under the current rules.
    if (report.status === 'rejected') {
      const back = document.createElement('button');
      back.className = 'mt-2 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-800';
      back.textContent = 'Вернуть на разбор';
      const outcome = document.createElement('span');
      outcome.className = 'ml-3 text-sm';
      bindBugDecision(back, outcome, report, 'retriage', false);
      card.append(document.createElement('br'), back, outcome);
    }
  }
  return card;
}

async function initSuperAdminDashboard(): Promise<void> {
  const loading = document.getElementById('super-admin-loading');
  const content = document.getElementById('super-admin-content');
  const rows = document.getElementById('super-admin-company-rows');
  if (!loading || !content || !rows) return;

  const meRes = await fetch('/api/super-admin/me', { credentials: 'same-origin' });
  if (!meRes.ok) {
    window.location.href = '/super-admin/login';
    return;
  }
  const me = (await meRes.json()) as { email: string; mailPortalUrl?: string | null; mailboxUrl?: string | null; filesUrl?:string|null; filesPortalUrl?:string|null };
  showMailPortal('super-files-link',me.filesPortalUrl);
  showMailPortal('super-mail-link', me.mailPortalUrl);
  const userEl = document.getElementById('super-admin-user');
  if (userEl) userEl.textContent = me.email;

  document.getElementById('super-admin-logout-button')?.addEventListener('click', async () => {
    await postJson('/api/super-admin/logout', {});
    window.location.href = '/super-admin/login';
  });

  void loadFunnel();
  void loadBugQueue();
  const refreshBugs = document.getElementById('bug-queue-refresh') as HTMLButtonElement | null;
  refreshBugs?.addEventListener('click', async () => {
    refreshBugs.disabled = true;
    const label = refreshBugs.textContent; refreshBugs.textContent = 'Проверяю…';
    try {
      const reports = await loadBugQueue(true);
      showBugDecision(reports ? 'Статусы обращений обновлены.' : 'Не удалось проверить статус. Решение повторно не отправлялось.', reports ? 'confirmed' : 'uncertain');
    } finally { refreshBugs.disabled = false; refreshBugs.textContent = label; }
  });

  const companiesRes = await fetch('/api/super-admin/companies', { credentials: 'same-origin' });
  const data = (await companiesRes.json()) as { companies: CompanyOverview[] };

  for (const company of data.companies) {
    const row = document.createElement('tr');
    row.className = 'cursor-pointer border-b border-slate-800 hover:bg-slate-800';

    const nameCell = document.createElement('td');
    nameCell.className = 'min-w-[260px] py-2 pr-4';
    nameCell.textContent = `${company.name} (${company.slug})`;
    const protection=document.createElement('div');
    protection.className='mt-2';
    nameCell.append(protection);
    const showProtected=()=>{
      protection.replaceChildren();
      const badge=document.createElement('span');badge.className='inline-flex rounded-lg bg-emerald-950 px-3 py-2 text-xs font-semibold text-emerald-300';badge.textContent='✓ Проверенная компания · защищена от удаления';protection.append(badge);
    };
    if(company.verified_at)showProtected();
    else{
      const verify=document.createElement('button');verify.type='button';verify.className='min-h-11 rounded-lg border border-slate-600 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-700';verify.textContent='Отметить проверенной';verify.setAttribute('aria-label',`Отметить проверенной компанию ${company.name} (${company.slug})`);
      protection.append(verify);
      protection.addEventListener('click',event=>event.stopPropagation());
      verify.addEventListener('click',()=>{
        if(protection.querySelector('form'))return;
        const form=document.createElement('form');form.className='mt-2 grid max-w-sm gap-2 rounded-lg border border-slate-600 bg-slate-950 p-3';
        const text=document.createElement('p');text.className='text-xs text-slate-300';text.textContent='Проверенная компания защищена от ручного и автоматического удаления. Обычного снятия защиты нет.';
        const label=document.createElement('label');label.className='grid gap-1 text-xs text-slate-300';label.textContent=`Для подтверждения введите ${company.slug}`;
        const input=document.createElement('input');input.type='text';input.required=true;input.autocomplete='off';input.spellcheck=false;input.maxLength=63;input.className='min-h-11 min-w-0 rounded-lg border border-slate-600 bg-slate-900 px-3 text-white';label.append(input);
        const controls=document.createElement('div');controls.className='flex flex-wrap gap-2';
        const submit=document.createElement('button');submit.type='submit';submit.disabled=true;submit.className='min-h-11 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40';submit.textContent='Подтвердить и защитить';
        const cancel=document.createElement('button');cancel.type='button';cancel.className='min-h-11 rounded-lg border border-slate-600 px-3 py-2 text-xs';cancel.textContent='Отмена';controls.append(submit,cancel);
        const status=document.createElement('p');status.className='text-xs text-amber-300';status.setAttribute('role','status');status.setAttribute('aria-live','polite');
        form.append(text,label,controls,status);protection.append(form);verify.hidden=true;input.focus();
        input.addEventListener('input',()=>{submit.disabled=input.value!==company.slug;});
        cancel.addEventListener('click',()=>{form.remove();verify.hidden=false;verify.focus();});
        form.addEventListener('submit',async event=>{
          event.preventDefault();if(input.value!==company.slug||submit.disabled)return;submit.disabled=true;cancel.disabled=true;input.disabled=true;status.textContent='Сохраняем защиту…';
          try{
            const result=await postJson(`/api/super-admin/companies/${company.id}/verify`,{confirmedSlug:input.value});
            if(!result.ok)throw new Error('verification_failed');
            const payload=result.data as {company?:{id?:string;verified_at?:string}};
            if(payload.company?.id!==company.id||typeof payload.company.verified_at!=='string'||!Number.isFinite(Date.parse(payload.company.verified_at)))throw new Error('invalid_verification');
            company.verified_at=payload.company.verified_at;showProtected();protection.tabIndex=-1;protection.focus();
          }catch{status.textContent='Не удалось подтвердить сохранение. Обновите страницу, чтобы проверить состояние компании.';}
        });
      });
    }

    const planCell = document.createElement('td');
    planCell.className = 'py-2 pr-4 text-slate-300';
    planCell.textContent = company.billing_status;

    const ownersCell = document.createElement('td');
    ownersCell.className = 'py-2 pr-4';
    ownersCell.textContent = company.owner_count;

    const employeesCell = document.createElement('td');
    employeesCell.className = 'py-2 pr-4';
    employeesCell.textContent = `${company.active_employee_count} активных, ${company.removed_employee_count} удалено`;

    const createdCell = document.createElement('td');
    createdCell.className = 'py-2 pr-4 text-slate-400';
    createdCell.textContent = new Date(company.created_at).toLocaleDateString('ru-RU');

    row.append(nameCell, planCell, ownersCell, employeesCell, createdCell);
    rows.append(row);

    const detailRow = document.createElement('tr');
    detailRow.className = 'hidden border-b border-slate-800 bg-slate-950';
    const detailCell = document.createElement('td');
    detailCell.colSpan = 5;
    detailCell.className = 'px-4 py-3';
    detailRow.append(detailCell);
    rows.append(detailRow);

    let loaded = false;
    row.addEventListener('click', async () => {
      detailRow.classList.toggle('hidden');
      if (loaded || detailRow.classList.contains('hidden')) return;
      loaded = true;
      const accountsRes = await fetch(`/api/super-admin/companies/${company.id}/accounts`, { credentials: 'same-origin' });
      const accountsData = (await accountsRes.json()) as { accounts: SuperAdminAccount[] };
      const list = document.createElement('ul') as HTMLUListElement;
      list.className = 'space-y-1 text-sm text-slate-300';
      for (const account of accountsData.accounts) {
        const item = document.createElement('li');
        item.textContent = `${fullName(account.first_name, account.last_name)} · ${account.email} · ${account.role} · ${account.status}`;
        list.append(item);
      }
      detailCell.append(list);
    });
  }

  loading.classList.add('hidden');
  content.classList.remove('hidden');
}

// Asked once per browser: the server records the agreement against a long-lived visitor cookie,
// so a reload, another page or a later visit does not ask again. A new edition of the documents
// changes the version and does ask again, because nobody agreed to text they never saw.
// Metrica sets tracking cookies, so it starts only after the visitor has agreed. Loading it on
// page load would make the banner decoration and the cookie policy false.
let metricaStarted = false;
function startMetrica(): void {
  if (metricaStarted) return;
  metricaStarted = true;
  const w = window as unknown as Record<string, unknown> & { ym?: ((...args: unknown[]) => void) & { a?: unknown[]; l?: number } };
  w.ym = w.ym ?? Object.assign((...args: unknown[]) => { (w.ym!.a = w.ym!.a ?? []).push(args); }, { l: Date.now() });
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://mc.yandex.ru/metrika/tag.js';
  document.head.append(script);
  w.ym!(112931077, 'init', { webvisor: true, clickmap: true, accurateTrackBounce: true, trackLinks: true });
}

async function initConsentBanner(): Promise<void> {
  // Started on visit, matching what the banner states ("продолжая, вы принимаете"). Waiting for
  // the click left Yandex's own code check unable to find the counter at all.
  startMetrica();

  const banner = document.getElementById('consent-banner');
  const accept = document.getElementById('consent-accept');
  if (!banner || !accept) return;

  try {
    const res = await fetch('/api/consent', { credentials: 'same-origin', signal: AbortSignal.timeout(5000) });
    if (!res.ok) return;
    const data = (await res.json()) as { accepted: boolean };
    if (data.accepted) return;
  } catch {
    // Never block the page on this: a failed check should leave the site usable, not stuck
    // behind a banner that cannot resolve itself.
    return;
  }

  banner.hidden = false;
  accept.addEventListener('click', async () => {
    banner.hidden = true;
    // Fired after hiding: the person has decided, and a slow network should not make the banner
    // linger as if the click did nothing.
    await postJson('/api/consent', {});
  });
}

const consentReady = initConsentBanner();
initSignupForm();
initVerifyForm();
initLoginPage();
void initDashboard();
initSuperAdminLoginForm();
void initSuperAdminDashboard();

initMailManagement();
void initMailOpen();

initFilesManagement();
